import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';
import AddBatchModal from '../components/batches/AddBatchModal';

export default function Batches() {
  const [searchParams] = useSearchParams();
  const initialCenterId = searchParams.get('center_id') || '';

  const [batches, setBatches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [centerFilter, setCenterFilter] = useState(initialCenterId);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  async function load() {
    try {
      setLoading(true);
      const params = { filter, limit: 100 };
      if (centerFilter) params.center_id = centerFilter;
      const result = await api.getBatches(params);
      setBatches(result.data.batches);
      setTotal(result.data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter, centerFilter]);

  useEffect(() => {
    api.getCoachingCenters({ filter: 'active', limit: 200 })
      .then(r => setCenters(r.data.centers))
      .catch(() => {});
  }, []);

  const centerName = (id) => centers.find(c => c.id === id)?.name || id;

  function formatDate(d) {
    if (!d) return 'Never';
    try { return formatDistanceToNow(new Date(d), { addSuffix: true }); }
    catch { return 'Unknown'; }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-teacher-text-primary">Batches</h1>
          <p className="text-teacher-text-secondary">A batch groups students under one or more teachers (one per subject typically)</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          + Add Batch
        </button>
      </div>

      <AddBatchModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={load}
        defaultCenterId={centerFilter}
      />

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <select
          value={centerFilter}
          onChange={(e) => setCenterFilter(e.target.value)}
          className="px-4 py-2 border border-teacher-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="">All Centers</option>
          {centers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-teacher-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="all">All Batches</option>
          <option value="active">Active Only</option>
          <option value="archived">Archived Only</option>
        </select>
      </div>

      <div className="mb-4 text-sm text-teacher-text-secondary">
        Showing {batches.length} of {total} batches
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">{error}</div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-teacher-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Center</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Classes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Teachers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-teacher-text-secondary">Loading batches...</td></tr>
              ) : batches.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-teacher-text-secondary">No batches found</td></tr>
              ) : (
                batches.map(b => (
                  <tr key={b.id} className="hover:bg-teacher-bg">
                    <td className="px-6 py-4 text-sm font-medium text-teacher-text-primary">{b.name}</td>
                    <td className="px-6 py-4 text-sm text-teacher-text-secondary">
                      <Link to={`/coaching-centers/${b.center_id}`} className="text-primary-600 hover:text-primary-900">
                        {centerName(b.center_id)}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-teacher-text-secondary">{(b.classes || []).join(', ') || '—'}</td>
                    <td className="px-6 py-4 text-sm font-medium text-primary-600">{(b.teacher_ids || []).length}</td>
                    <td className="px-6 py-4 text-sm font-medium text-primary-600">{b.total_students || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        b.is_active !== false
                          ? 'bg-green-100 text-green-700'
                          : 'bg-teacher-bg-secondary text-teacher-text-secondary'
                      }`}>{b.is_active !== false ? 'Active' : 'Archived'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-teacher-text-secondary">{formatDate(b.created_at)}</td>
                    <td className="px-6 py-4 text-sm">
                      <Link to={`/batches/${b.id}`} className="text-primary-600 hover:text-primary-900">View →</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
